/**
 * Media Defaults Configuration
 *
 * Default image URLs served from CloudFront CDN (media.tracverse.com)
 * These are system assets stored in talktrail/assets/defaults/
 *
 * IMPORTANT: These files must exist on S3 at the specified paths!
 * Upload default images to: s3://[bucket]/talktrail/assets/defaults/
 */

const MEDIA_CDN_URL = 'https://media.tracverse.com';

// Local fallback images (in public folder) - used if CDN images fail
const LOCAL_FALLBACK = '/images/defaults';

export const MEDIA_DEFAULTS = {
  // Profile defaults - CDN URLs
  PROFILE_AVATAR: `${MEDIA_CDN_URL}/talktrail/assets/defaults/profile-avatar.webp`,
  PROFILE_BACKGROUND: `${MEDIA_CDN_URL}/talktrail/assets/defaults/profile-background.webp`,

  // Content defaults
  DISCUSSION_BANNER: `${MEDIA_CDN_URL}/talktrail/assets/defaults/discussion-banner.webp`,
  ARTICLE_COVER: `${MEDIA_CDN_URL}/talktrail/assets/defaults/article-cover.webp`,

  // Community defaults
  COMMUNITY_AVATAR: `${MEDIA_CDN_URL}/talktrail/assets/defaults/community-avatar.webp`,
  COMMUNITY_BANNER: `${MEDIA_CDN_URL}/talktrail/assets/defaults/community-banner.webp`,

  // Local fallbacks (in public/images/defaults/)
  LOCAL_PROFILE_AVATAR: `${LOCAL_FALLBACK}/profile-avatar.svg`,
  LOCAL_PROFILE_BACKGROUND: `${LOCAL_FALLBACK}/profile-background.svg`,
};

/**
 * Avatar progression paths (unlocked by level)
 * Each path has 10 levels (level-01 to level-10)
 */
export const AVATAR_PROGRESSION_PATHS = {
  atom: `${MEDIA_CDN_URL}/talktrail/assets/progression/atom`,
  brain: `${MEDIA_CDN_URL}/talktrail/assets/progression/brain`,
  tree: `${MEDIA_CDN_URL}/talktrail/assets/progression/tree`,
};

/**
 * Get progression avatar URL for a specific path and level
 * @param {string} path - 'atom', 'brain', or 'tree'
 * @param {number} level - 1-10
 * @returns {string} Full URL to the progression avatar
 */
export function getProgressionAvatar(path, level) {
  const clampedLevel = Math.min(10, Math.max(1, level));
  const paddedLevel = String(clampedLevel).padStart(2, '0');
  return `${AVATAR_PROGRESSION_PATHS[path]}/level-${paddedLevel}.webp`;
}

/**
 * Helper to get user avatar with fallback to default
 * @param {object} user - User object with avatar/thumbnail fields
 * @returns {string} Avatar URL or default
 */
export function getUserAvatar(user) {
  if (user?.moderation?.profileRestrictions?.avatarRestricted) {
    return MEDIA_DEFAULTS.LOCAL_PROFILE_AVATAR;
  }
  return user?.avatar || user?.thumbnail || MEDIA_DEFAULTS.LOCAL_PROFILE_AVATAR;
}

/**
 * Helper to get user background with fallback to default
 * @param {object} user - User object with backgroundImage field
 * @returns {string|null} Background URL or null
 */
export function getUserBackground(user) {
  if (user?.moderation?.profileRestrictions?.backgroundRestricted) {
    return null;
  }
  return user?.backgroundImage || null;
}

export default MEDIA_DEFAULTS;
